/**
 * Master Print Station (/print-hub) — control + monitor surface for the
 * globally-running Print Hub (see PrintHubContext). Connect the Tecor
 * once, press Start, and the Hub keeps printing queued labels in the
 * BACKGROUND even after you leave this page (do temp records, print more
 * labels, etc.) as long as the browser tab stays open. Admins only.
 */
import api, { withSite } from "@/lib/api";
import { useAuth } from "@/contexts/AuthContext";
import { useSite } from "@/contexts/SiteContext";
import { useTecor } from "@/contexts/TecorContext";
import { usePrintHub } from "@/contexts/PrintHubContext";
import { Button } from "@/components/ui/button";
import { toast } from "sonner";
import {
  Usb, Play, Pause, RotateCcw, CheckCircle2, AlertTriangle, Printer,
  Loader2, ShieldAlert, RefreshCw, Wifi,
} from "lucide-react";

const STATUS_STYLE = {
  pending: "bg-amber-100 text-amber-800",
  printing: "bg-sky-100 text-sky-800",
  completed: "bg-eucalyptus-100 text-eucalyptus-900",
  failed: "bg-red-100 text-red-700",
};

function fmtTime(iso) {
  if (!iso) return "";
  try { return new Date(iso).toLocaleTimeString([], { hour: "2-digit", minute: "2-digit" }); }
  catch { return ""; }
}

export default function PrintHub() {
  const { user } = useAuth();
  const { activeSite } = useSite();
  const { supported, connected, deviceName, connect } = useTecor();
  const {
    hubEnabled, setHubEnabled, hubWorking, hubRecent, hubPending, hubLastError, refreshHub,
  } = usePrintHub();

  const siteId = activeSite?.id;
  const isAdmin = user?.role === "admin";
  const canRun = supported && connected;

  const handleConnect = async () => {
    try {
      await connect();
      toast.success("Tecor TR-422B connected");
    } catch (e) {
      toast.error(e?.message || "Connection cancelled");
    }
  };

  const reprint = async (job) => {
    try {
      await api.post(`/print-queue/${job.id}/reprint`, {}, withSite(siteId));
      toast.success(`Re-queued ${job.product_name || job.label_type}`);
      refreshHub();
    } catch (e) {
      toast.error(e?.message || "Couldn't re-queue");
    }
  };

  if (!isAdmin) {
    return (
      <div className="max-w-md mx-auto surface p-8 text-center space-y-3" data-testid="hub-denied">
        <ShieldAlert className="w-8 h-8 text-amber-600 mx-auto" />
        <h1 className="font-display text-2xl font-bold">Admins only</h1>
        <p className="text-charcoal-soft text-sm">
          The Master Print Station is restricted to admins. Ask a manager to open it on the station computer.
        </p>
      </div>
    );
  }

  return (
    <div className="max-w-4xl mx-auto space-y-6" data-testid="print-hub">
      <div>
        <div className="label-eyebrow">Master Print Station</div>
        <h1 className="font-display text-4xl font-bold tracking-tight">Print Hub</h1>
        <p className="text-charcoal-soft mt-2 max-w-2xl">
          Connect the Tecor TR-422B and press Start. The Hub then prints every label staff send —
          for <b>{activeSite?.name || "this site"}</b> — and <b>keeps running in the background</b> even if you
          leave this page to do other tasks. Just keep this browser tab open.
        </p>
      </div>

      {/* Control bar */}
      <div className="surface p-5 space-y-4" data-testid="hub-controls">
        <div className="flex flex-wrap items-center gap-3">
          <div className="flex items-center gap-2 text-sm mr-auto">
            <Usb className="w-4 h-4 text-eucalyptus-700" />
            {connected ? (
              <span className="flex items-center gap-1 text-eucalyptus-800 font-medium">
                <CheckCircle2 className="w-4 h-4" /> {deviceName || "Tecor TR-422B"} connected
              </span>
            ) : supported ? (
              <span className="text-charcoal-soft">Printer not connected</span>
            ) : (
              <span className="flex items-center gap-1 text-amber-700">
                <AlertTriangle className="w-4 h-4" /> Use Chrome or Edge on desktop to run the Hub
              </span>
            )}
          </div>

          {supported && !connected && (
            <Button data-testid="hub-connect" variant="outline" className="gap-2" onClick={handleConnect}>
              <Usb className="w-4 h-4" /> Connect printer
            </Button>
          )}

          {hubEnabled ? (
            <Button data-testid="hub-stop" className="gap-2 bg-red-600 hover:bg-red-500 text-white" onClick={() => setHubEnabled(false)}>
              <Pause className="w-4 h-4" /> Pause printing
            </Button>
          ) : (
            <Button data-testid="hub-start" disabled={!canRun} className="gap-2 bg-ochre-600 hover:bg-ochre-400 text-white disabled:opacity-40" onClick={() => setHubEnabled(true)}>
              <Play className="w-4 h-4" /> Start printing
            </Button>
          )}
        </div>

        {/* Status line */}
        <div className="flex flex-wrap items-center gap-4 text-sm">
          <span className="inline-flex items-center gap-2">
            {hubWorking
              ? <><Loader2 className="w-4 h-4 animate-spin text-sky-600" /> Printing…</>
              : hubEnabled && connected
                ? <><span className="w-2 h-2 rounded-full bg-eucalyptus-600 animate-pulse" /> Live — watching the queue</>
                : <><span className="w-2 h-2 rounded-full bg-charcoal-soft/40" /> Paused</>}
          </span>
          <span className="text-charcoal-soft" data-testid="hub-pending">
            <b className="text-charcoal">{hubPending}</b> waiting
          </span>
          {hubEnabled && (
            <span className="inline-flex items-center gap-1 text-charcoal-soft" data-testid="hub-bg-note">
              <Wifi className="w-3.5 h-3.5" /> Runs in the background while this tab is open
            </span>
          )}
        </div>

        {/* Warnings */}
        {hubEnabled && !connected && (
          <div className="flex items-start gap-2 text-sm rounded-lg bg-amber-50 border border-amber-200 p-3 text-amber-800" data-testid="hub-warn-disconnected">
            <AlertTriangle className="w-4 h-4 mt-0.5 shrink-0" />
            Printer disconnected — labels are queuing but NOT printing. Reconnect the Tecor (it re-links automatically when the computer wakes).
          </div>
        )}
        {!hubEnabled && (
          <div className="flex items-start gap-2 text-sm rounded-lg bg-eucalyptus-50 border border-eucalyptus-200 p-3 text-eucalyptus-900" data-testid="hub-warn-paused">
            <AlertTriangle className="w-4 h-4 mt-0.5 shrink-0" />
            Printing is paused — staff labels will sit in the queue until you press <b>Start printing</b>.
          </div>
        )}
        {hubLastError && (
          <div className="flex items-start gap-2 text-sm rounded-lg bg-red-50 border border-red-200 p-3 text-red-700" data-testid="hub-error">
            <AlertTriangle className="w-4 h-4 mt-0.5 shrink-0" /> Last error: {hubLastError}
          </div>
        )}
      </div>

      {/* Live queue */}
      <div className="surface p-5" data-testid="hub-queue">
        <div className="flex items-center justify-between mb-3">
          <div className="label-eyebrow">Live queue · last 10</div>
          <button onClick={refreshHub} className="text-charcoal-soft hover:text-charcoal" aria-label="Refresh" data-testid="hub-refresh">
            <RefreshCw className="w-4 h-4" />
          </button>
        </div>
        {hubRecent.length === 0 ? (
          <div className="text-center text-charcoal-soft text-sm py-8">
            No jobs yet. When staff hit Print, jobs appear here.
          </div>
        ) : (
          <ul className="divide-y divide-[color:var(--border)]">
            {hubRecent.map((j) => (
              <li key={j.id} className="flex items-center gap-3 py-2.5" data-testid={`hub-job-${j.id}`}>
                <span className={`text-[10px] font-mono uppercase px-2 py-0.5 rounded-full ${STATUS_STYLE[j.status] || "bg-gray-100"}`}>
                  {j.status}
                </span>
                <div className="min-w-0 flex-1">
                  <div className="font-medium truncate">
                    {j.product_name || j.label_type} <span className="text-charcoal-soft font-normal">· {j.copies}× · {j.label_type}</span>
                  </div>
                  <div className="text-[11px] text-charcoal-soft truncate">
                    {j.source_device || "device"} · {j.user_name} · {fmtTime(j.printed_at || j.created_at)}
                    {j.status === "failed" && j.error ? ` · ${j.error}` : ""}
                  </div>
                </div>
                <Button data-testid={`hub-reprint-${j.id}`} size="sm" variant="outline" className="gap-1 shrink-0" onClick={() => reprint(j)}>
                  <RotateCcw className="w-3.5 h-3.5" /> Reprint
                </Button>
              </li>
            ))}
          </ul>
        )}
      </div>

      <p className="text-[11px] text-charcoal-soft flex items-center gap-1">
        <Printer className="w-3.5 h-3.5" />
        Tip: bookmark this page on the station computer. Start once — it keeps printing all shift, even on other pages.
      </p>
    </div>
  );
}
